export type ContentBlock =
  | { type: "heading"; text: string }
  | { type: "para"; text: string }
  | { type: "quote"; text: string }
  | { type: "list"; items: string[] };

export type Article = {
  id: number;
  slug: string;
  title: string;
  excerpt: string;
  category: string;
  categoryName: string;
  cover: string;
  author: string;
  date: string;
  readTime: string;
  tags: string[];
  source: string;
  content: ContentBlock[];
};

export const articles: Article[] = [
  {
    id: 1,
    slug: "host-read-wrong-name-before-crown",
    title: "冠军揭晓前五秒，主持人念错了名字",
    excerpt:
      "聚光灯已经打到一号选手身上，大屏幕却停在五号，主持人那半句没念完的话成了全场最安静的五秒钟。",
    category: "fails",
    categoryName: "翻车现场",
    cover:
      "https://readdy.ai/api/search-image?query=Elegant%20pageant%20stage%20with%20a%20golden%20crown%20on%20a%20velvet%20pillow%20under%20warm%20spotlight%2C%20red%20curtain%20background%2C%20editorial%20photography%2C%20warm%20amber%20tones&width=800&height=600&seq=cover-crown-01&orientation=landscape",
    author: "前排观察员",
    date: "2026-08-18",
    readTime: "5 分钟",
    tags: ["主持人失误", "宣布环节", "翻车复盘"],
    source: "整理自现场观众投稿与公开赛事回放",
    content: [
      {
        type: "para",
        text: "宣布冠军本该是整场比赛最稳的环节，台词提前彩排、名单反复核对，连信封的拆封顺序都有人盯着。可偏偏就是这种最不该出问题的环节，最容易被五秒钟的注意力松动打败。",
      },
      { type: "heading", text: "现场到底发生了什么" },
      {
        type: "para",
        text: "当晚的宣布流程是：主持人先念出季军、亚军，再拆开写着冠军名字的信封。问题出在拆信封之前——大屏幕的程序切到了冠军候选展示，而聚光灯控制台按原定脚本，提前把光束打向了一号选手。",
      },
      {
        type: "para",
        text: "一号选手下意识往前迈了半步，台下已经开始鼓掌。主持人低头看信封，念出的是五号的名字。掌声戛然而止，光束停在原地没有跟过去，那半秒钟的空气安静到能听见空调声。",
      },
      {
        type: "quote",
        text: "台下那一刻没人敢先鼓掌，因为大家都不确定自己是不是听错了。",
      },
      { type: "heading", text: "翻车是怎么被圆回来的" },
      {
        type: "list",
        items: [
          "主持人没有重复名单，而是先请五号上台，用一句把掌声送给真正的冠军把节奏接回去。",
          "灯光师在三秒内把追光移向五号，同时保留了一束柔光打在一号身上，避免她独自站在暗处。",
          "一号选手在台上主动走上前拥抱了五号，这个即兴动作后来成了当晚传播最广的画面。",
        ],
      },
      { type: "heading", text: "吃瓜看点" },
      {
        type: "para",
        text: "这段翻车之所以被反复讨论，不是因为有人做错了什么大事，而是它把直播的不可控完整地展示了出来。彩排能覆盖流程，覆盖不了现场几百个并行操作里任何一环的提前或滞后。",
      },
      {
        type: "para",
        text: "事后回看，真正值得记住的反而是选手的反应：一号没有表现出失落，五号没有露出尴尬，两个人在台上完成了一次教科书级别的情绪兜底。",
      },
    ],
  },
  {
    id: 2,
    slug: "runway-twist-split-save",
    title: "走猫步崴了脚，她用一个劈叉把场子救了回来",
    excerpt:
      "高跟鞋踩到裙摆的那一瞬间明显踉跄，所有人都以为要摔，结果她顺势下了一个劈叉，全场掌声比原定编排还响。",
    category: "funny",
    categoryName: "搞笑名场面",
    cover:
      "https://readdy.ai/api/search-image?query=Warm%20lit%20pageant%20runway%20with%20long%20elegant%20evening%20gown%20fabric%20draped%20on%20the%20floor%2C%20spotlights%2C%20editorial%20photography%2C%20warm%20amber%20and%20cream%20tones&width=800&height=600&seq=cover-runway-02&orientation=landscape",
    author: "瓜棚主理人",
    date: "2026-08-14",
    readTime: "4 分钟",
    tags: ["救场", "临场反应", "名场面"],
    source: "整理自赛事回放与选手赛后采访",
    content: [
      {
        type: "para",
        text: "走秀环节的意外通常发生在裙摆和高跟鞋之间。这次也不例外：一段长达十米的跑道，她在第七米踩到了自己礼服的侧摆。",
      },
      { type: "heading", text: "三秒里的三次判断" },
      {
        type: "para",
        text: "从画面看，她的重心先是向前倾，随即右脚下意识找支撑，但高跟鞋的高度让这个支撑变成了一个不稳的角度。换成大多数人，下一步就是扶地或者摔倒。",
      },
      {
        type: "list",
        items: [
          "第一秒：放弃站直，主动降低重心，避免膝盖直接被砸。",
          "第二秒：把踉跄的方向转化成向下的动作，顺势坐成劈叉。",
          "第三秒：抬头、微笑、把手抬到编排里本来就有的一个定点姿势上。",
        ],
      },
      {
        type: "quote",
        text: "她说那一刻脑子里只有一个念头：既然已经往下掉了，那就掉得好看一点。",
      },
      { type: "heading", text: "为什么这次救场这么好看" },
      {
        type: "para",
        text: "因为她的动作没有中断感。观众看到的不是失误加掩饰，而是一个完整的动作序列，只不过最后一个动作不在原编排里。掌声之所以比原定编排还响，正是因为大家刚经历了一次共同的屏息。",
      },
      {
        type: "para",
        text: "赛后有评委在采访里提到，走秀考核看的从来不只是台步本身，还有出现意外时对节奏的处理能力。这段劈叉后来被不少舞蹈和形体老师当作临场反应的案例来讲。",
      },
    ],
  },
  {
    id: 3,
    slug: "judges-holding-back-laugh-top-answers",
    title: "评委席集体憋笑：问答环节最尴尬的十个回答",
    excerpt:
      "镜头扫过评委席的那几秒，信息量比整场比赛还大。有人低头写字，有人盯着水杯，有人已经笑到肩膀在抖。",
    category: "judge",
    categoryName: "评委表情",
    cover:
      "https://readdy.ai/api/search-image?query=Judge%20panel%20table%20with%20scorecards%20pens%20and%20water%20glasses%20under%20warm%20spotlights%2C%20blurred%20stage%20behind%2C%20editorial%20photography%2C%20warm%20amber%20and%20cream%20tones&width=800&height=600&seq=cover-judge-03&orientation=landscape",
    author: "前排观察员",
    date: "2026-08-09",
    readTime: "6 分钟",
    tags: ["评委反应", "问答环节", "表情管理"],
    source: "整理自公开赛事片段与观众视角视频",
    content: [
      {
        type: "para",
        text: "问答环节是选美比赛里最考验标准答案的环节，也是最容易出岔子的环节。选手临场紧张、题目措辞抽象、麦克风收音延迟，任何一个变量都能把一句准备好的答案拧成完全不同的意思。",
      },
      { type: "heading", text: "评委的表情比分数诚实" },
      {
        type: "para",
        text: "有意思的是，真正被记住的往往不是选手说了什么，而是评委听到之后的反应。我们按出现频率整理了几种典型的评委表情：",
      },
      {
        type: "list",
        items: [
          "低头写字型：其实没写任何内容，只是需要一个不直视选手的理由。",
          "盯着水杯型：端起来又放下，全程没有真的喝水。",
          "交换眼神型：左右两位评委用一个极其短暂的侧目完成了全部沟通。",
          "肩膀微抖型：已经笑出声了，但还在努力用咳嗽压回去。",
          "礼貌点头型：表情纹丝不动，只是点头的速度比平时快了一点点。",
        ],
      },
      {
        type: "quote",
        text: "评委的职业素养不允许他们当场评价，但身体反应往往比嘴快。",
      },
      { type: "heading", text: "尴尬归尴尬，别急着下结论" },
      {
        type: "para",
        text: "需要说明的是，问答环节的尴尬大多来自紧张而不是能力。台上那股压力很难被没站上去过的人理解：几百双眼睛、十几台摄像机、倒计时牌就在视线边缘。",
      },
      {
        type: "para",
        text: "所以这个栏目更愿意把镜头对准共同的尴尬——评委也好、选手也好、主持人也好，那一刻大家都在同一场事故里，谁也没法优雅离场，这才是乐子的来源。",
      },
    ],
  },
  {
    id: 4,
    slug: "dress-zipper-backstage-moment",
    title: "礼服拉链崩开的瞬间，后台都发生了什么",
    excerpt:
      "上场前九十秒，礼服侧缝的拉链突然滑开。后台四个人同时围上去，用的全是平时排练时想不到的办法。",
    category: "fails",
    categoryName: "翻车现场",
    cover:
      "https://readdy.ai/api/search-image?query=Backstage%20dressing%20area%20with%20garment%20bags%20mirrors%20and%20warm%20bulb%20lights%2C%20empty%20chair%2C%20editorial%20photography%2C%20warm%20amber%20and%20brown%20tones&width=800&height=600&seq=cover-backstage-04&orientation=landscape",
    author: "瓜棚主理人",
    date: "2026-08-05",
    readTime: "5 分钟",
    tags: ["后台", "服装事故", "应急"],
    source: "整理自后台工作人员投稿",
    content: [
      {
        type: "para",
        text: "舞台上的每一个从容，背后都有一群人在后台跑动。礼服出状况是后台最常见的应急之一，而这次的时间只剩九十秒。",
      },
      { type: "heading", text: "九十秒里的分工" },
      {
        type: "list",
        items: [
          "造型师负责固定面料，用别针从内侧把开缝处收拢，确保正面看不出鼓包。",
          "服装组去仓库翻出同色系的披帛，作为遮挡的备用方案。",
          "场务同步确认出场顺序，评估能不能往后挪两位争取时间。",
          "队友站在镜子前反复确认走动、转身、抬手三个动作会不会再次拉扯。",
        ],
      },
      {
        type: "quote",
        text: "后来她说，真正让她冷静下来的不是别针，而是身边四个人没有一个人露出慌张的表情。",
      },
      { type: "heading", text: "上台之后" },
      {
        type: "para",
        text: "最终她没有使用披帛，别针方案撑住了整段走秀。直到回到侧幕，她才在没有人看见的角落把一直提着的那口气吐出来。台上那两分钟的稳定，是靠后台九十秒的密度换来的。",
      },
      {
        type: "para",
        text: "这类翻车现场往往没有舞台上的画面可看，但从复盘角度反而更有价值：一场比赛的应急能力，几乎全藏在这些看不见的九十秒里。",
      },
    ],
  },
  {
    id: 5,
    slug: "swimsuit-round-wrong-bgm",
    title: "泳装环节放错了 BGM，全场跟着一起摇摆",
    excerpt:
      "本该是轻快节奏的段落，音响切成了慢板抒情。选手愣了一拍，然后干脆把走位改成了自己的节拍。",
    category: "funny",
    categoryName: "搞笑名场面",
    cover:
      "https://readdy.ai/api/search-image?query=Warm%20bright%20stage%20with%20colorful%20stage%20lights%20and%20speaker%20equipment%2C%20dance%20floor%20reflections%2C%20editorial%20photography%2C%20warm%20amber%20and%20golden%20tones&width=800&height=600&seq=cover-swim-05&orientation=landscape",
    author: "前排观察员",
    date: "2026-07-30",
    readTime: "4 分钟",
    tags: ["音响事故", "临场反应", "节奏"],
    source: "整理自赛事回放",
    content: [
      {
        type: "para",
        text: "音响事故是舞台最常见的意外之一，也是最难掩饰的：音乐一响，全场立刻就知道了。这次的问题在于，错的不是音量，而是曲目本身。",
      },
      { type: "heading", text: "从愣住到接管节拍" },
      {
        type: "para",
        text: "第一位上场的选手在听到慢板抒情的瞬间明显停了一拍，脚已经迈出去，身体却还停在起始位。但只用了大约两秒，她就放弃了原本排练的轻快走位，改成按慢板节奏延展动作。",
      },
      {
        type: "quote",
        text: "后台后来复盘时说，那两秒里她做的决定不是要不要停，而是要不要换一套跳法。",
      },
      {
        type: "list",
        items: [
          "音响师在第二位选手上场前完成了曲目切换，事故只影响了一段。",
          "主持人在串场时顺势开了个玩笑，把尴尬转成了全场的第一次笑声。",
          "后面几位选手被临时提醒可以按自己的节奏来，反而走得更放松。",
        ],
      },
      { type: "heading", text: "为什么这反而成了名场面" },
      {
        type: "para",
        text: "因为观众看到了一次真实的临场改编。原编排再完美，也只是纸面上的方案；能在音乐不对的情况下依然把整段走完，并且走得漂亮，这种能力是排练不出来的。",
      },
    ],
  },
  {
    id: 6,
    slug: "hot-mic-after-elimination",
    title: "淘汰名单念完后，那句麦克风没关的话",
    excerpt:
      "音乐已经响起，灯光开始转移，侧幕的麦克风却还开着。一句话飘进了全场的耳朵，也把当晚的讨论度直接拉满。",
    category: "elimination",
    categoryName: "淘汰时刻",
    cover:
      "https://readdy.ai/api/search-image?query=Vintage%20microphone%20on%20a%20stand%20under%20warm%20spotlight%20with%20dark%20stage%20and%20red%20curtains%20behind%2C%20editorial%20photography%2C%20warm%20amber%20tones%2C%20dramatic%20mood&width=800&height=600&seq=cover-mic-06&orientation=landscape",
    author: "瓜棚主理人",
    date: "2026-07-24",
    readTime: "5 分钟",
    tags: ["麦克风", "淘汰环节", "现场"],
    source: "整理自现场观众投稿",
    content: [
      {
        type: "para",
        text: "淘汰环节的流程通常是固定的：念名单、播放告别音乐、灯光渐暗、选手走下舞台。所有人的注意力都在台上，很少有人留意那些还没关掉的收声设备。",
      },
      { type: "heading", text: "那句话是怎么传出来的" },
      {
        type: "para",
        text: "当晚，被淘汰的选手走到侧幕后，与队友拥抱。此时主持人的手持麦克风已经挂回支架，但侧幕的通道麦克风仍在工作。一句原本只说给身边人听的话，被完整地送进了全场音响。",
      },
      {
        type: "quote",
        text: "那句话并不尖锐，甚至有点温柔，但因为出现的时间和地点都不对，反而成了整晚最难忘的一句。",
      },
      { type: "heading", text: "吃瓜的分寸" },
      {
        type: "para",
        text: "这类片段传播力极强，也最容易被二次加工。作为围观者，我们能做的是尽量保留当时的完整语境：她说这句话时面对的是谁、前后发生了什么、语气是什么样。脱离语境的转述，往往比原话更伤人。",
      },
      {
        type: "list",
        items: [
          "保留完整语境，不截取单句做标题。",
          "不臆测选手之间的关系，评论只针对公开画面。",
          "把重点放回舞台表现本身，而不是私人情绪。",
        ],
      },
    ],
  },
  {
    id: 7,
    slug: "talent-show-improvised-coda",
    title: "才艺表演即兴加戏，把评委看愣了",
    excerpt:
      "原定两分钟的才艺，她演到一分半时临时加了一段。台下评委的表情从鼓掌变成了放下笔，认真看完了最后三十秒。",
    category: "funny",
    categoryName: "搞笑名场面",
    cover:
      "https://readdy.ai/api/search-image?query=Warm%20lit%20stage%20with%20a%20single%20performance%20spotlight%20and%20flowing%20fabric%20in%20motion%2C%20dramatic%20warm%20amber%20tones%2C%20editorial%20photography&width=800&height=600&seq=cover-talent-07&orientation=landscape",
    author: "前排观察员",
    date: "2026-07-18",
    readTime: "4 分钟",
    tags: ["才艺表演", "即兴", "评委反应"],
    source: "整理自赛事回放",
    content: [
      {
        type: "para",
        text: "才艺环节通常是整场比赛里准备时间最长的部分，也最容易出现排练痕迹过重的问题。而这次被记住的，恰恰是那段没有排练过的三十秒。",
      },
      { type: "heading", text: "加戏的那三十秒" },
      {
        type: "para",
        text: "按编排，她的表演应该在一分半时进入收尾。但当晚现场的掌声让她临时改变了计划：她把原定的单独收尾延长成了一个与伴奏的即兴应答，动作也比排练时更放开。",
      },
      {
        type: "quote",
        text: "评委席上有人把原本已经拿起来的笔又放了回去，重新靠在椅背上。",
      },
      {
        type: "list",
        items: [
          "即兴部分没有脱离作品的整体情绪，衔接是自然的。",
          "她控制了时长，加戏后仍在规定时间的三秒内结束。",
          "表演结束后她主动解释了加戏的原因，把现场气氛又带高了一次。",
        ],
      },
      { type: "heading", text: "为什么值得单独拿出来讲" },
      {
        type: "para",
        text: "因为即兴不等于失控。一段好的即兴，需要极强的作品熟悉度和现场判断力。这也是为什么很多评委在点评时会特意提到：能在规定动作之外做出反应的人，往往对舞台有真正的掌控。",
      },
    ],
  },
  {
    id: 8,
    slug: "qa-not-answering-question",
    title: "问答答非所问，主持人接不住的三连问",
    excerpt:
      "问题问的是理想，回答讲的是家乡小吃。主持人试图拉回来，结果第二个问题又跑偏了，全场就这样看着对话一路飘走。",
    category: "awkward",
    categoryName: "尴尬瞬间",
    cover:
      "https://readdy.ai/api/search-image?query=Empty%20interview%20stool%20and%20stand%20microphone%20under%20a%20warm%20spotlight%20on%20a%20dark%20stage%2C%20editorial%20photography%2C%20warm%20amber%20tones%2C%20quiet%20mood&width=800&height=600&seq=cover-qa-08&orientation=landscape",
    author: "瓜棚主理人",
    date: "2026-07-12",
    readTime: "5 分钟",
    tags: ["问答环节", "尴尬", "主持人"],
    source: "整理自公开赛事片段",
    content: [
      {
        type: "para",
        text: "问答环节的尴尬有时并不来自紧张，而是来自理解偏差。题目问的是价值观，选手回答的是事实，两边都在认真讲，却完全没在同一频道上。",
      },
      { type: "heading", text: "一次典型的跑偏" },
      {
        type: "para",
        text: "主持人问：如果可以改变家乡的一件事，你会选什么？选手从家乡的小吃讲起，讲到小时候的街巷，再讲到外婆的院子。内容很动人，但始终没有回答会改变什么。",
      },
      {
        type: "para",
        text: "主持人试图把话题拉回来：那如果一定要选一件。选手接的是：我选那家早点铺，因为它现在已经拆了。全场笑了，但这个笑并不是因为幽默。",
      },
      {
        type: "quote",
        text: "主持人后来在串场时说了一句，看来我们对改变这个词的理解不太一样。这句话本身也成了名场面。",
      },
      { type: "heading", text: "怎么看待这种尴尬" },
      {
        type: "para",
        text: "与其把它当成选手不行，不如把它看成直播里的信息损耗。题目越抽象，跑偏概率越高，而现场的紧张会进一步压缩选手的思考时间。围观的时候笑一笑没问题，但没必要上纲上线。",
      },
    ],
  },
  {
    id: 9,
    slug: "top-three-photo-moment",
    title: "冠亚季军合影名场面：站位之下的表情管理",
    excerpt:
      "合影只有八秒，但八秒里的走位、让位、眼神交流，足够让观众剪出十几版慢动作解析。",
    category: "judge",
    categoryName: "评委表情",
    cover:
      "https://readdy.ai/api/search-image?query=Elegant%20stage%20with%20three%20golden%20trophies%20and%20flowers%20arranged%20under%20warm%20spotlights%2C%20red%20curtain%20background%2C%20editorial%20photography%2C%20warm%20amber%20tones&width=800&height=600&seq=cover-photo-09&orientation=landscape",
    author: "前排观察员",
    date: "2026-07-06",
    readTime: "4 分钟",
    tags: ["合影", "表情管理", "名场面"],
    source: "整理自赛事回放与观众视角视频",
    content: [
      {
        type: "para",
        text: "前三名合影是每场比赛的固定收尾，也是被慢放次数最多的画面之一。八秒里发生的调度，比很多人想象中复杂。",
      },
      { type: "heading", text: "八秒里的三次微调" },
      {
        type: "list",
        items: [
          "第一次：冠军被引导到中间位置时脚步略慢了半拍，两侧的选手同时向内让了半步，队列因此向一侧偏了三厘米。",
          "第二次：摄影师喊看这里之前，亚军主动调整了捧花的手，避免挡住冠军的号码牌。",
          "第三次：闪光灯连续两次后，三人的笑容同时松了一次又立刻收回，完成了真实情绪与镜头需求的切换。",
        ],
      },
      {
        type: "quote",
        text: "慢放能看清所有细节，但慢放看不到的是她们在当时其实只有不到一秒可以决定怎么做。",
      },
      { type: "heading", text: "吃瓜也需要边界" },
      {
        type: "para",
        text: "这类解析最容易滑向解读站队。我们的原则是：只描述画面里能看到的动作和时间顺序，不替选手补内心戏。表情管理本身就是专业能力的一部分，被讨论是正常的，被定罪就不正常了。",
      },
    ],
  },
  {
    id: 10,
    slug: "backstage-interview-silence",
    title: "后台采访名场面，选手一句话让全场安静",
    excerpt:
      "采访间原本很吵，她说完那句话之后，连举着收音杆的工作人员都愣了一下，整整两秒没人接话。",
    category: "fails",
    categoryName: "翻车现场",
    cover:
      "https://readdy.ai/api/search-image?query=Backstage%20interview%20corner%20with%20a%20microphone%20on%20a%20table%20and%20warm%20practical%20lamps%2C%20soft%20blurred%20curtain%20behind%2C%20editorial%20photography%2C%20warm%20amber%20and%20cream%20tones&width=800&height=600&seq=cover-interview-10&orientation=landscape",
    author: "瓜棚主理人",
    date: "2026-06-28",
    readTime: "4 分钟",
    tags: ["后台采访", "沉默", "名场面"],
    source: "整理自后台采访片段投稿",
    content: [
      {
        type: "para",
        text: "后台采访通常安排在比赛结束之后，环境嘈杂、时间紧张，问题也多为常规的此刻心情如何。偶尔会出现一句让所有人都停下来的回答。",
      },
      { type: "heading", text: "那两秒的安静" },
      {
        type: "para",
        text: "记者问：今天最想感谢谁？现场还在不停有人进出、搬道具、喊名字。她回答的不是父母，也不是教练，而是一句关于那个曾经劝我别报名的人的回答。声音不大，但采访间的动作明显停了一拍。",
      },
      {
        type: "quote",
        text: "收音杆在她说完之后仍然举着，工作人员没有立刻收回，像是在等一个还没出现的后半句。",
      },
      { type: "heading", text: "为什么这类片段值得被记录" },
      {
        type: "para",
        text: "选美比赛被讨论最多的往往是外表和话题，但真正能留下来的，常常是这些不带舞台感的瞬间。它们提醒围观者：台上那些被反复解析的名场面背后，是一群有具体经历的人。",
      },
    ],
  },
];